import {
    TaskViewCreateDTO,
    TaskViewDTO,
    TaskViewLayoutDTO,
    TaskViewLayoutUpdateDTO,
    TaskViewUpdateDTO,
} from 'models/dto/tasks/taskViewDTO';
import AppError from 'utils/appError';
import http from './httpService';
import { getTaskViewLayoutSummary, logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';

const latestRevisionHeader = 'x-latest-revision-id';

type AppErrorWithLatestRevision = AppError & { latestRevision?: string };

export const getLatestRevisionFromError = (err: unknown): string | undefined => {
  const response = (err as { response?: { headers?: Record<string, string | string[] | undefined> } })?.response;
  const value = response?.headers?.[latestRevisionHeader];

  if (!value) return (err as { latestRevision?: string })?.latestRevision;

  return Array.isArray(value) ? value.join(',') : String(value);
};

const normalizeTaskViewDto = (dto: TaskViewDTO): TaskViewDTO => ({
  ...dto,
  revision: dto.revision ? String(dto.revision) : '',
});

export async function apiGetTaskViews(accessToken: string): Promise<TaskViewDTO[]> {
  logTaskViewDiagnostic('api.views.owned.get.start');
  try {
    const ar = await http.get<TaskViewDTO[]>('/taskviews', http.getRequestConfig(accessToken));

    const views = (Array.isArray(ar.data) ? ar.data : []).map(normalizeTaskViewDto);
    logTaskViewDiagnostic('api.views.owned.get.success', {
      count: views.length,
      viewKeys: views.map((view) => `view-api-${view.taskViewId}`),
    });

    return views;
  } catch (err) {
    logTaskViewDiagnostic('api.views.owned.get.error', {
      code: (err as { code?: string })?.code,
      status: (err as { response?: { status?: number } })?.response?.status,
    });

    throw AppError.fromApiError(err);
  }
}

export async function apiGetSharedTaskViews(accessToken: string): Promise<TaskViewDTO[]> {
  logTaskViewDiagnostic('api.views.shared.get.start');
  try {
    const ar = await http.get<TaskViewDTO[]>('/taskviews/shared', http.getRequestConfig(accessToken));

    const views = (Array.isArray(ar.data) ? ar.data : []).map(normalizeTaskViewDto);
    logTaskViewDiagnostic('api.views.shared.get.success', {
      count: views.length,
      viewKeys: views.map((view) => `view-api-${view.taskViewId}`),
    });

    return views;
  } catch (err) {
    logTaskViewDiagnostic('api.views.shared.get.error', {
      code: (err as { code?: string })?.code,
      status: (err as { response?: { status?: number } })?.response?.status,
    });

    throw AppError.fromApiError(err);
  }
}

export async function apiCreateTaskView(dto: TaskViewCreateDTO, accessToken: string): Promise<TaskViewDTO> {
  try {
    const ar = await http.post<TaskViewDTO>('/taskviews', dto, http.getRequestConfig(accessToken));

    return normalizeTaskViewDto(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTaskView(
  id: number,
  dto: TaskViewUpdateDTO,
  accessToken: string,
): Promise<TaskViewDTO> {
  try {
    const ar = await http.put<TaskViewDTO>(`/taskviews/${id}`, dto, http.getRequestConfig(accessToken));

    return normalizeTaskViewDto(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteTaskView(id: number, accessToken: string): Promise<void> {
  try {
    await http.delete(`/taskviews/${id}`, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiShareTaskView(
  id: number,
  isShared: boolean,
  revision: string,
  accessToken: string,
): Promise<TaskViewDTO> {
  try {
    const shareDTO: Pick<TaskViewDTO, 'isShared' | 'revision'> = { isShared, revision };
    const ar = await http.put<TaskViewDTO>(`/taskviews/${id}/share`, shareDTO, http.getRequestConfig(accessToken));

    return normalizeTaskViewDto(ar.data);
  } catch (err) {
    const appError = AppError.fromApiError(err) as AppErrorWithLatestRevision;
    appError.latestRevision = getLatestRevisionFromError(err);

    throw appError;
  }
}

export async function apiGetTaskViewLayout(accessToken: string): Promise<TaskViewLayoutDTO> {
  logTaskViewDiagnostic('api.layout.get.start');
  try {
    const ar = await http.get<TaskViewLayoutDTO>('/taskviews/layout', http.getRequestConfig(accessToken));

    logTaskViewDiagnostic('api.layout.get.success', {
      layout: getTaskViewLayoutSummary(ar.data),
    });

    return ar.data;
  } catch (err) {
    logTaskViewDiagnostic('api.layout.get.error', {
      code: (err as { code?: string })?.code,
      status: (err as { response?: { status?: number } })?.response?.status,
    });

    throw AppError.fromApiError(err);
  }
}

export async function apiSaveTaskViewLayout(layout: TaskViewLayoutDTO, accessToken: string): Promise<TaskViewLayoutDTO> {
  logTaskViewDiagnostic('api.layout.put.start', {
    layout: getTaskViewLayoutSummary(layout),
  });
  try {
    const dto: TaskViewLayoutUpdateDTO = { layout };
    const ar = await http.put<TaskViewLayoutDTO>('/taskviews/layout', dto, http.getRequestConfig(accessToken));

    logTaskViewDiagnostic('api.layout.put.success', {
      layout: getTaskViewLayoutSummary(ar.data),
    });

    return ar.data;
  } catch (err) {
    logTaskViewDiagnostic('api.layout.put.error', {
      code: (err as { code?: string })?.code,
      status: (err as { response?: { status?: number } })?.response?.status,
      latestRevision: getLatestRevisionFromError(err),
    });

    const appError = AppError.fromApiError(err) as AppErrorWithLatestRevision;
    appError.latestRevision = getLatestRevisionFromError(err);

    throw appError;
  }
}
